import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";

import { queryClient } from "App";
import tagConstants from "constants/Tag";
import UserService from "services/User";
import RegionService from "services/Regions";
import TagService from "services/Tag";
import CaseService from "services/Case";
import EntityService from "services/Entity";
import CustomFieldService from "services/CustomField";
import {
  getCasesTableColumns,
  getCasesTableFilters,
} from "routes/case/CasesTableColumnsAndFilters";
import { buildCustomFieldFilters } from "components/BaseTableV3/table-helpers";
import { addCustomFieldsColumns, addResizedColumns } from "./useTableConfig";
import { getCountryByCode } from "helpers/countries";
import { formatUsersForFilter } from "helpers/user";
import { caseKeys } from "routes/case/queryKeys";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";

const handleFetchRegionsAndCountries = async () => {
  const rawRegions = await RegionService.getRegions();
  const regions = rawRegions.map(({ id, name, countries = [] }) => ({
    id,
    name,
    extraData: { countries },
  }));

  const allCountries = rawRegions.reduce((countries, region) => {
    countries.push(...region.countries);
    return countries;
  }, []);
  allCountries.sort();
  const countries = Array.from(new Set(allCountries)).map(country => ({
    id: country,
    name: getCountryByCode(country)?.text,
    value: country,
    key: country,
  }));
  return { regions, countries };
};

const handleFetchUsers = async () => {
  const rawUsers = await UserService.getUsers();
  return formatUsersForFilter(rawUsers);
};

const handleFetchInvestigationStatuses = async () => {
  const investigationStatuses =
    await CaseService.getInvestigationStatusesForFilters();
  return investigationStatuses;
};

const handleFetchCustomFields = async () => {
  const customFields = await CaseService.getCustomFields();
  const entitiesCustomFields = await EntityService.getCustomFields();
  const caseConfigs =
    await CustomFieldService.getCustomFieldConfigsForModel("Case");
  const entityConfigs =
    await CustomFieldService.getCustomFieldConfigsForModel("Entity");
  return [
    {
      accessorPrefix: "custom_fields",
      headerPrefix: "Case",
      fields: customFields,
      model: "Case",
      configs: caseConfigs,
    },
    {
      accessorPrefix: "entity_custom_fields",
      headerPrefix: "Entity",
      fields: entitiesCustomFields,
      model: "Entity",
      configs: entityConfigs,
    },
  ];
};

const handleFetchTags = async className => {
  const taggableModels = tagConstants["taggableModels"];
  const model = Object.keys(taggableModels).find(
    key => taggableModels[key] === className
  );
  const tags = await TagService.getTagOptions({ model });

  return tags;
};

export const useCaseTable = ({
  className = "Case",
  campaignId = null,
  entityId = null,
}) => {
  const { enable_aegis_automation_status } = useRuvixxFlags();

  const { data: regionsAndCountries, isSuccess: isRegionsAndCountriesFetched } =
    useQuery({
      queryKey: ["regions"],
      queryFn: handleFetchRegionsAndCountries,
    });

  const { data: users, isSuccess: isUsersFetched } = useQuery({
    queryKey: ["users"],
    queryFn: handleFetchUsers,
  });

  const {
    data: investigationStatuses,
    isSuccess: isInvestigationStatusesFetched,
  } = useQuery({
    queryKey: ["investigation-statuses"],
    queryFn: handleFetchInvestigationStatuses,
  });

  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", "options", className],
    queryFn: () => handleFetchTags(className),
  });

  const { data: customFields, isSuccess: isCustomFieldsFetched } = useQuery({
    queryKey: ["custom-fields", className],
    queryFn: handleFetchCustomFields,
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const queryKey = useMemo(
    () => caseKeys.list({ campaignId, entityId }),
    [campaignId, entityId]
  );

  const invalidateData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryKey]);

  const deleteCase = useCallback(
    async id => {
      await CaseService.deleteCase(id);
      await invalidateData();
    },
    [invalidateData]
  );

  const filters = useMemo(() => {
    if (
      !isRegionsAndCountriesFetched ||
      !isUsersFetched ||
      !isInvestigationStatusesFetched ||
      !isTagsFetched ||
      !isCustomFieldsFetched
    ) {
      return [];
    }

    const finalFilters = getCasesTableFilters({
      regions: regionsAndCountries?.regions,
      countries: regionsAndCountries?.countries,
      users,
      investigationStatuses,
      tags,
      campaignId,
      entityId,
      enableAegisAutomationStatus: enable_aegis_automation_status,
    });

    if (isCustomFieldsFetched) {
      finalFilters.push(
        ...buildCustomFieldFilters(
          customFields,
          finalFilters.map(f => f.title)
        )
      );
    }

    return finalFilters;
  }, [
    regionsAndCountries,
    users,
    investigationStatuses,
    tags,
    customFields,
    campaignId,
    entityId,
    isRegionsAndCountriesFetched,
    isUsersFetched,
    isInvestigationStatusesFetched,
    isTagsFetched,
    isCustomFieldsFetched,
    enable_aegis_automation_status,
  ]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched || !isCustomFieldsFetched) return [];
    const initialColumns = getCasesTableColumns({
      campaignId,
      invalidateData,
      deleteCase,
      enableAegisAutomationStatus: enable_aegis_automation_status,
    });
    const casesTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.cases`,
      {}
    );

    const withCustomFieldsColumns = addCustomFieldsColumns({
      columns: initialColumns,
      customFields,
      visibleColumns: casesTableSettings?.visible_columns || [],
    });
    const withResizedColumns = addResizedColumns({
      columns: withCustomFieldsColumns,
      resizedColumns: casesTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    customFields,
    tableSettings,
    campaignId,
    isTableSettingsFetched,
    isCustomFieldsFetched,
    invalidateData,
    deleteCase,
    enable_aegis_automation_status,
  ]);

  return {
    filters,
    columns,
    tags,
    customFields,
    queryKey,
    invalidateData,
  };
};
